import React, { useState, useMemo } from "react";
import {
  Box,
  Container,
  Text,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Button,
  Badge,
  SimpleGrid,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  X,
  ChevronDown,
  Sparkles,
  HelpCircle,
  Briefcase,
  Laptop,
  GraduationCap,
  Award,
  MessageCircleQuestion,
  Mail,
  ArrowRight,
  CheckCircle2,
  BookOpen,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";

const MotionBox = motion(Box);

const categories = [
  { id: "all", label: "All Questions", icon: HelpCircle },
  { id: "remote", label: "Virtual & Remote", icon: Laptop },
  { id: "eligibility", label: "Eligibility & Applying", icon: GraduationCap },
  { id: "certification", label: "Fees & Certification", icon: Award },
];

const faqData = [
  {
    id: 1,
    category: "internships",
    categoryLabel: "Internships & Projects",
    title: "What type of internship is provided by Gyan?",
    content:
      "Gyan offers structured, project-based virtual internships across in-demand domains including Full-Stack Web Development, Machine Learning, Data Science, Cloud Computing, UI/UX Design, and Digital Marketing. Interns build industry-grade projects to showcase on their portfolios.",
    highlights: ["Hands-on coding projects", "Guided mentorship", "Real-world tech stacks"],
  },
  {
    id: 2,
    category: "internships",
    categoryLabel: "Internships & Projects",
    title: "What is a project-based internship?",
    content:
      "In a project-based internship, you solve practical problem statements instead of performing repetitive clerical tasks. Under the guidance of experienced mentors, you go through the full lifecycle: requirement gathering, system architecture, development, testing, and deployment.",
    highlights: ["Complete development lifecycle", "Industry mentor reviews", "GitHub portfolio showcase"],
  },
  {
    id: 3,
    category: "internships",
    categoryLabel: "Internships & Projects",
    title: "What does the Virtual Internships Program include?",
    content:
      "Activities and support vary by internship track. Depending on the programme, interns may work with project documentation, complete assigned learning activities, and receive milestone feedback. Certificates are issued only after the applicable completion requirements are met. Letters of Recommendation may be considered for interns who demonstrate strong performance, meaningful contributions, and professional commitment; issuance is at the authorised recommender's discretion and is not guaranteed.",
    highlights: ["Track-specific project activities", "Milestone feedback where available", "Performance-based LOR consideration"],
  },
  {
    id: 4,
    category: "internships",
    categoryLabel: "Internships & Projects",
    title: "Does the duration affect the quality of the internship?",
    content:
      "The internship track roadmaps are organised across 12 weeks. Individual listings may have their own configured durations; review each listing for its duration and requirements.",
    highlights: ["12-week track milestones", "Review each listing for details"],
  },
  {
    id: 5,
    category: "remote",
    categoryLabel: "Virtual & Remote",
    title: "What is a virtual internship and how does remote work work?",
    content:
      "A virtual internship allows you to work 100% remotely from any location. You communicate with mentors, submit tasks, and collaborate with peers through modern digital tools like Discord, GitHub, Slack, and Google Meet without any relocation requirements.",
    highlights: ["100% remote flexibility", "Modern collaboration tools", "Zero relocation hassle"],
  },
  {
    id: 6,
    category: "remote",
    categoryLabel: "Virtual & Remote",
    title: "What are the key benefits of virtual internships?",
    content:
      "Virtual internships eliminate commuting and accommodation expenses, allow you to balance academics with professional work, build essential remote-first collaboration skills, and connect with peers and mentors across the country.",
    highlights: ["Cost savings", "Work-from-anywhere freedom", "Self-paced study balance"],
  },
  {
    id: 7,
    category: "remote",
    categoryLabel: "Virtual & Remote",
    title: "How flexible is the internship schedule?",
    content:
      "Our programs are created specifically for college students and working learners. The deadlines and milestones are self-paced, allowing you to easily manage college classes, exams, assignments, and personal commitments.",
    highlights: ["Self-paced weekly milestones", "Exam break friendly", "Part-time commitment"],
  },
  {
    id: 8,
    category: "eligibility",
    categoryLabel: "Eligibility & Applying",
    title: "Who is eligible to apply for Gyan internships?",
    content:
      "Students currently enrolled in undergraduate (B.Tech, BCA, B.Sc, etc.) or postgraduate (M.Tech, MCA, M.Sc, etc.) degrees, fresh graduates, and self-taught developers looking to build verified practical experience are all welcome to apply.",
    highlights: ["Undergraduates & Postgraduates", "Freshers & Career switchers", "Passionate learners"],
  },
  {
    id: 9,
    category: "eligibility",
    categoryLabel: "Eligibility & Applying",
    title: "How do I apply and start my internship?",
    content:
      "Browse available domains on the Internships page, pick the domain that aligns with your career goals, complete the quick enrollment form, and you will receive your project kickoff kit and mentor onboarding details instantly.",
    highlights: ["Instant kickoff kit", "Simple online enrollment", "Direct mentor access"],
  },
  {
    id: 10,
    category: "certification",
    categoryLabel: "Fees & Certification",
    title: "Why do some internship tracks have an enrollment fee?",
    content:
      "The fee enables us to provide personalized 1-on-1 mentor guidance, comprehensive code reviews, dedicated live doubt resolution, cloud learning resources, and secure digital credential verification.",
    highlights: ["1-on-1 mentor guidance", "Cloud learning infrastructure", "Verified certificate hosting"],
  },
  {
    id: 11,
    category: "certification",
    categoryLabel: "Fees & Certification",
    title: "How do certificate and Letter of Recommendation decisions work?",
    content:
      "A certificate may be issued upon meeting the programme's completion requirements, subject to the applicable internship policy. Letters of Recommendation may be considered for interns who demonstrate strong performance, meaningful contributions, and professional commitment during the internship. Issuance is at the discretion of the authorised recommender and is not guaranteed upon completion.",
    highlights: ["Certificate subject to completion policy", "Performance-based LOR consideration", "LOR is not guaranteed"],
  },
  {
    id: 12,
    category: "certification",
    categoryLabel: "Fees & Certification",
    title: "How does Gyan help in future career opportunities?",
    content:
      "By completing authentic real-world projects, you gain tangible proof of work for your resume and GitHub. Furthermore, standout interns gain referrals, network with experienced industry mentors, and receive interview preparation guidance.",
    highlights: ["Resume proof-of-work", "Referral opportunities", "Interview preparation"],
  },
];

const Faq = () => {
  const { isAdmin } = useSelector((state) => state.user || {});
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [openId, setOpenId] = useState(1);

  const toggleItem = (id) => {
    setOpenId((prev) => (prev === id ? null : id));
  };

  const filteredFaqs = useMemo(() => {
    return faqData.filter((item) => {
      const matchesCategory =
        selectedCategory === "all" || item.category === selectedCategory;
      const query = searchQuery.trim().toLowerCase();
      const matchesQuery =
        !query ||
        item.title.toLowerCase().includes(query) ||
        item.content.toLowerCase().includes(query) ||
        item.categoryLabel.toLowerCase().includes(query) ||
        (item.highlights &&
          item.highlights.some((h) => h.toLowerCase().includes(query)));
      return matchesCategory && matchesQuery;
    });
  }, [selectedCategory, searchQuery]);

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-br, #163a7a, #1e4b9e, #0c2252)"
      color="white"
      fontFamily="Figtree, sans-serif"
      position="relative"
      overflowX="hidden"
    >
      <NavBar isAdmin={isAdmin} />

      {/* AMBIENT GLOW BLOBS & PATTERN */}
      <Box
        position="absolute"
        top="10%"
        left="15%"
        w="500px"
        h="500px"
        bg="blue.400"
        opacity={0.15}
        filter="blur(120px)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="10%"
        right="10%"
        w="400px"
        h="400px"
        bg="green.400"
        opacity={0.12}
        filter="blur(120px)"
        pointerEvents="none"
      />
      <Box
        position="absolute"
        inset="0"
        opacity="0.05"
        backgroundImage="radial-gradient(circle at 1px 1px, white 1px, transparent 0)"
        backgroundSize="28px 28px"
        pointerEvents="none"
      />

      {/* HERO & CONTENT CONTAINER */}
      <Container maxW="880px" pt={{ base: "85px", md: "95px" }} pb="60px" position="relative" zIndex="1">
        {/* HEADER */}
        <MotionBox
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          textAlign="center"
          maxW="680px"
          mx="auto"
        >
          {/* BADGE */}
          <Flex
            align="center"
            justify="center"
            gap={1.5}
            px={3}
            py={1}
            mb={3}
            mx="auto"
            w="fit-content"
            borderRadius="full"
            bg="whiteAlpha.200"
            border="1px solid rgba(255, 255, 255, 0.2)"
            backdropFilter="blur(10px)"
          >
            <Sparkles size={14} color="#5EEAD4" />
            <Text fontSize="11.5px" fontWeight="700" letterSpacing="0.4px" color="teal.200" textTransform="uppercase">
              Help & Support Center
            </Text>
          </Flex>

          {/* MAIN TITLE */}
          <Text
            as="h1"
            fontSize={{ base: "26px", sm: "32px", md: "40px" }}
            fontWeight="800"
            lineHeight="1.2"
            letterSpacing="-0.5px"
            color="white"
          >
            Frequently Asked{" "}
            <Text as="span" bgGradient="linear(to-r, green.300, teal.200)" bgClip="text">
              Questions
            </Text>
          </Text>

          <Text
            mt={2}
            fontSize={{ base: "14px", md: "15.5px" }}
            color="whiteAlpha.800"
            lineHeight="1.5"
            maxW="560px"
            mx="auto"
          >
            Everything you need to know about Gyan internships, mentorship, and certifications.
          </Text>

          {/* SEARCH BAR */}
          <Box mt={4} maxW="560px" mx="auto">
            <InputGroup
              size="md"
              bg="whiteAlpha.100"
              backdropFilter="blur(20px)"
              borderRadius="12px"
              border="1px solid rgba(255, 255, 255, 0.2)"
              transition="all 0.25s ease"
              _hover={{ borderColor: "rgba(94, 234, 212, 0.5)" }}
              _focusWithin={{
                borderColor: "teal.300",
                boxShadow: "0 0 0 2px rgba(45, 212, 191, 0.3)",
              }}
            >
              <InputLeftElement pointerEvents="none" pl={3} height="100%">
                <Search size={17} color="#94A3B8" />
              </InputLeftElement>
              <Input
                placeholder="Search questions (e.g. certificate, fees, mentor, remote)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fontSize="14px"
                pl="40px"
                pr={searchQuery ? "40px" : "14px"}
                py="10px"
                height="44px"
                color="white"
                border="none"
                _placeholder={{ color: "whiteAlpha.600" }}
                _focus={{ boxShadow: "none" }}
              />
              {searchQuery && (
                <InputRightElement pr={2} height="100%">
                  <Button
                    size="xs"
                    variant="ghost"
                    onClick={() => setSearchQuery("")}
                    color="whiteAlpha.700"
                    _hover={{ color: "white", bg: "whiteAlpha.200" }}
                    borderRadius="full"
                    p={1}
                    height="24px"
                    width="24px"
                  >
                    <X size={14} />
                  </Button>
                </InputRightElement>
              )}
            </InputGroup>
          </Box>
        </MotionBox>

        {/* CATEGORY FILTER PILLS */}
        <Flex
          mt={5}
          mb={4}
          gap={2}
          wrap="wrap"
          justify="center"
          align="center"
        >
          {categories.map((cat) => {
            const IconComponent = cat.icon;
            const isSelected = selectedCategory === cat.id;
            const count =
              cat.id === "all"
                ? faqData.length
                : faqData.filter((f) => f.category === cat.id).length;

            return (
              <Button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                size="sm"
                px={3}
                py={1.5}
                height="34px"
                borderRadius="10px"
                fontWeight="600"
                fontSize="12.5px"
                display="flex"
                alignItems="center"
                gap={1.5}
                transition="all 0.2s ease"
                bg={isSelected ? "linear-gradient(to right, #059669, #0d9488)" : "whiteAlpha.100"}
                color="white"
                border="1px solid"
                borderColor={isSelected ? "rgba(94, 234, 212, 0.6)" : "rgba(255, 255, 255, 0.15)"}
                backdropFilter="blur(16px)"
                boxShadow={isSelected ? "0 4px 15px rgba(13, 148, 136, 0.35)" : "none"}
                _hover={{
                  bg: isSelected ? "linear-gradient(to right, #047857, #0f766e)" : "whiteAlpha.200",
                  borderColor: "rgba(255, 255, 255, 0.3)",
                }}
              >
                <IconComponent size={14} />
                <span>{cat.label}</span>
                <Badge
                  ml={0.5}
                  fontSize="10px"
                  borderRadius="full"
                  px={1.5}
                  py={0.2}
                  bg={isSelected ? "whiteAlpha.300" : "rgba(255, 255, 255, 0.12)"}
                  color="white"
                >
                  {count}
                </Badge>
              </Button>
            );
          })}
        </Flex>

        {/* RESULTS INFO BAR */}
        <Flex
          justify="space-between"
          align="center"
          mb={3.5}
          px={1}
          fontSize="12.5px"
          color="whiteAlpha.700"
        >
          <Text>
            Showing <Text as="span" fontWeight="700" color="teal.200">{filteredFaqs.length}</Text> {filteredFaqs.length === 1 ? "question" : "questions"}
            {searchQuery && (
              <Text as="span"> for &quot;<Text as="span" color="white" fontWeight="600">{searchQuery}</Text>&quot;</Text>
            )}
          </Text>

          {openId !== null && (
            <Button
              variant="link"
              size="xs"
              color="teal.200"
              fontWeight="600"
              _hover={{ color: "teal.100", textDecoration: "underline" }}
              onClick={() => setOpenId(null)}
            >
              Collapse all
            </Button>
          )}
        </Flex>

        {/* FAQS ACCORDION LIST */}
        <Box>
          {filteredFaqs.length === 0 ? (
            /* EMPTY STATE */
            <MotionBox
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              textAlign="center"
              py={10}
              px={4}
              bg="whiteAlpha.100"
              backdropFilter="blur(20px)"
              borderRadius="16px"
              border="1px dashed rgba(255, 255, 255, 0.2)"
            >
              <Box
                w="48px"
                h="48px"
                mx="auto"
                mb={3}
                borderRadius="full"
                bg="whiteAlpha.200"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <HelpCircle size={24} color="#5EEAD4" />
              </Box>
              <Text fontSize="16px" fontWeight="700" color="white">
                No matching questions found
              </Text>
              <Text mt={1} fontSize="13px" color="whiteAlpha.700">
                Try different search terms or clear filters.
              </Text>
              <Button
                mt={4}
                size="xs"
                bg="linear-gradient(to right, #059669, #0d9488)"
                color="white"
                _hover={{ bg: "linear-gradient(to right, #047857, #0f766e)" }}
                borderRadius="8px"
                px={3}
                py={1.5}
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("all");
                }}
              >
                Reset Search Filters
              </Button>
            </MotionBox>
          ) : (
            <Flex direction="column" gap={2.5}>
              {filteredFaqs.map((faq, index) => {
                const isOpen = openId === faq.id;
                return (
                  <MotionBox
                    key={faq.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: index * 0.02 }}
                    borderRadius="12px"
                    bg={isOpen ? "whiteAlpha.200" : "whiteAlpha.100"}
                    backdropFilter="blur(20px)"
                    border="1px solid"
                    borderColor={isOpen ? "rgba(94, 234, 212, 0.4)" : "rgba(255, 255, 255, 0.12)"}
                    overflow="hidden"
                    _hover={{
                      borderColor: isOpen ? "rgba(94, 234, 212, 0.6)" : "rgba(255, 255, 255, 0.25)",
                      transform: "translateY(-1px)",
                    }}
                  >
                    {/* ACCORDION HEADER / BUTTON */}
                    <Box
                      as="button"
                      w="100%"
                      textAlign="left"
                      py={{ base: 3, md: 3.5 }}
                      px={{ base: 3.5, md: 4.5 }}
                      onClick={() => toggleItem(faq.id)}
                      cursor="pointer"
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={3}
                      bg="transparent"
                      _focus={{ outline: "none" }}
                    >
                      <Flex align="center" gap={{ base: 2.5, md: 3 }} flex="1">
                        {/* NUMBER / ICON BADGE */}
                        <Flex
                          align="center"
                          justify="center"
                          w={{ base: "26px", md: "28px" }}
                          h={{ base: "26px", md: "28px" }}
                          borderRadius="7px"
                          bg={isOpen ? "linear-gradient(to right, #059669, #0d9488)" : "whiteAlpha.200"}
                          color="white"
                          fontSize="12px"
                          fontWeight="700"
                          flexShrink={0}
                        >
                          {index + 1 < 10 ? `0${index + 1}` : index + 1}
                        </Flex>

                        <Box>
                          <Text
                            fontSize={{ base: "14.5px", md: "15.5px" }}
                            fontWeight="600"
                            color="white"
                            lineHeight="1.35"
                          >
                            {faq.title}
                          </Text>
                        </Box>
                      </Flex>

                      {/* TOGGLE CHEVRON */}
                      <Flex
                        align="center"
                        justify="center"
                        w="28px"
                        h="28px"
                        borderRadius="full"
                        bg={isOpen ? "whiteAlpha.300" : "whiteAlpha.100"}
                        color={isOpen ? "teal.200" : "whiteAlpha.700"}
                        flexShrink={0}
                        transition="all 0.2s ease"
                      >
                        <ChevronDown
                          size={15}
                          style={{
                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.25s ease",
                          }}
                        />
                      </Flex>
                    </Box>

                    {/* EXPANDABLE BODY */}
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <MotionBox
                          key="content"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25, ease: "easeInOut" }}
                          overflow="hidden"
                        >
                          <Box
                            px={{ base: 3.5, md: 4.5 }}
                            pb={{ base: 3.5, md: 4 }}
                            pt={0}
                            borderTop="1px solid"
                            borderColor="rgba(255, 255, 255, 0.1)"
                          >
                            <Text
                              mt={2.5}
                              fontSize={{ base: "13.5px", md: "14.5px" }}
                              color="whiteAlpha.900"
                              lineHeight="1.6"
                            >
                              {faq.content}
                            </Text>

                            {/* HIGHLIGHT CHIPS */}
                            {faq.highlights && (
                              <Flex mt={3} gap={1.5} wrap="wrap">
                                {faq.highlights.map((item, hIdx) => (
                                  <Flex
                                    key={hIdx}
                                    align="center"
                                    gap={1}
                                    px={2}
                                    py={0.5}
                                    borderRadius="6px"
                                    bg="rgba(45, 212, 191, 0.12)"
                                    border="1px solid rgba(94, 234, 212, 0.25)"
                                    color="teal.200"
                                    fontSize="11.5px"
                                    fontWeight="500"
                                  >
                                    <CheckCircle2 size={12} color="#5EEAD4" />
                                    <span>{item}</span>
                                  </Flex>
                                ))}
                              </Flex>
                            )}
                          </Box>
                        </MotionBox>
                      )}
                    </AnimatePresence>
                  </MotionBox>
                );
              })}
            </Flex>
          )}
        </Box>

        {/* TRUST & CONFIDENCE STATS */}
        <SimpleGrid
          columns={{ base: 1, sm: 3 }}
          spacing={3}
          mt={8}
          mb={6}
        >
          <Flex
            align="center"
            gap={2.5}
            p={3}
            borderRadius="10px"
            bg="whiteAlpha.100"
            backdropFilter="blur(16px)"
            border="1px solid rgba(255, 255, 255, 0.12)"
          >
            <Flex
              w="34px"
              h="34px"
              borderRadius="8px"
              bg="rgba(94, 234, 212, 0.2)"
              align="center"
              justify="center"
              color="teal.200"
              flexShrink={0}
            >
              <Award size={16} />
            </Flex>
            <Box>
              <Text fontSize="13px" fontWeight="700" color="white" lineHeight="1.2">
                Verified Credentials
              </Text>
              <Text fontSize="11px" color="whiteAlpha.700">
                Industry-recognized certificates
              </Text>
            </Box>
          </Flex>

          <Flex
            align="center"
            gap={2.5}
            p={3}
            borderRadius="10px"
            bg="whiteAlpha.100"
            backdropFilter="blur(16px)"
            border="1px solid rgba(255, 255, 255, 0.12)"
          >
            <Flex
              w="34px"
              h="34px"
              borderRadius="8px"
              bg="rgba(56, 189, 248, 0.2)"
              align="center"
              justify="center"
              color="cyan.200"
              flexShrink={0}
            >
              <BookOpen size={16} />
            </Flex>
            <Box>
              <Text fontSize="13px" fontWeight="700" color="white" lineHeight="1.2">
                Expert Mentorship
              </Text>
              <Text fontSize="11px" color="whiteAlpha.700">
                Guidance from industry leaders
              </Text>
            </Box>
          </Flex>

          <Flex
            align="center"
            gap={2.5}
            p={3}
            borderRadius="10px"
            bg="whiteAlpha.100"
            backdropFilter="blur(16px)"
            border="1px solid rgba(255, 255, 255, 0.12)"
          >
            <Flex
              w="34px"
              h="34px"
              borderRadius="8px"
              bg="rgba(16, 185, 129, 0.2)"
              align="center"
              justify="center"
              color="green.200"
              flexShrink={0}
            >
              <Laptop size={16} />
            </Flex>
            <Box>
              <Text fontSize="13px" fontWeight="700" color="white" lineHeight="1.2">
                100% Virtual Flexibility
              </Text>
              <Text fontSize="11px" color="whiteAlpha.700">
                Self-paced learning milestones
              </Text>
            </Box>
          </Flex>
        </SimpleGrid>

        {/* STILL HAVE QUESTIONS CONTACT CARD */}
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          p={{ base: 4, md: 6 }}
          borderRadius="16px"
          bg="whiteAlpha.100"
          backdropFilter="blur(24px)"
          border="1px solid rgba(255, 255, 255, 0.2)"
          position="relative"
          overflow="hidden"
        >
          <Flex
            direction={{ base: "column", sm: "row" }}
            align={{ base: "flex-start", sm: "center" }}
            justify="space-between"
            gap={4}
            position="relative"
            zIndex={1}
          >
            <Box maxW="480px">
              <Flex align="center" gap={1.5} mb={1}>
                <MessageCircleQuestion size={18} color="#5EEAD4" />
                <Text fontSize="11.5px" fontWeight="700" color="teal.200" textTransform="uppercase" letterSpacing="0.4px">
                  Need More Help?
                </Text>
              </Flex>
              <Text fontSize={{ base: "17px", md: "20px" }} fontWeight="700" color="white" lineHeight="1.25">
                Still have questions? We&apos;re here to help.
              </Text>
              <Text mt={1} fontSize="13px" color="whiteAlpha.800" lineHeight="1.5">
                Can&apos;t find what you&apos;re looking for? Reach out to our academic team anytime.
              </Text>
            </Box>

            <Flex gap={2} wrap="wrap" w={{ base: "100%", sm: "auto" }}>
              <Button
                as={Link}
                to="/internship"
                size="sm"
                bg="linear-gradient(to right, #059669, #0d9488)"
                color="white"
                borderRadius="10px"
                px={4}
                height="38px"
                fontSize="13.5px"
                fontWeight="600"
                rightIcon={<ArrowRight size={14} />}
                _hover={{
                  bg: "linear-gradient(to right, #047857, #0f766e)",
                  boxShadow: "0 4px 15px rgba(13, 148, 136, 0.4)",
                  transform: "translateY(-1px)",
                }}
                w={{ base: "100%", sm: "auto" }}
              >
                Browse Internships
              </Button>

              <Button
                as="a"
                href="mailto:support@aim4u.co.in"
                size="sm"
                variant="outline"
                borderColor="rgba(255, 255, 255, 0.3)"
                color="white"
                borderRadius="10px"
                px={4}
                height="38px"
                fontSize="13.5px"
                fontWeight="600"
                leftIcon={<Mail size={14} />}
                _hover={{
                  bg: "whiteAlpha.200",
                  borderColor: "rgba(255, 255, 255, 0.5)",
                }}
                w={{ base: "100%", sm: "auto" }}
              >
                Contact Support
              </Button>
            </Flex>
          </Flex>
        </MotionBox>
      </Container>

      <Footer />
    </Box>
  );
};

export default Faq;
